import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Phone, MapPin, Heart, Shield, Award, Calendar, Mail, Smile, Baby, Wrench, Scissors, Activity, AlertCircle, GraduationCap } from 'lucide-react'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import drAishwaryaImage from '@/assets/aishwarya_1.jpg'
import '@/styles/animations.css'

const services = [
  {
    id: 'preventive',
    title: 'Preventive Care',
    description: 'Regular checkups, cleanings, and preventive treatments to maintain optimal oral health',
    icon: Shield,
    color: 'bg-green-100 text-green-600',
    treatments: [
      'Regular Dental Checkups',
      'Professional Teeth Cleaning',
      'Fluoride Treatment',
      'Dental Sealants',
      'Oral Cancer Screening',
      'Gum Disease Prevention'
    ]
  },
  {
    id: 'cosmetic',
    title: 'Cosmetic Dentistry',
    description: 'Enhance your smile with advanced cosmetic dental treatments',
    icon: Smile,
    color: 'bg-pink-100 text-pink-600',
    treatments: [
      'Teeth Whitening',
      'Dental Veneers',
      'Smile Makeover',
      'Dental Bonding',
      'Gum Contouring',
      'Composite Fillings'
    ]
  },
  {
    id: 'restorative',
    title: 'Restorative Dentistry',
    description: 'Restore function and appearance of damaged or missing teeth',
    icon: Wrench,
    color: 'bg-blue-100 text-blue-600',
    treatments: [
      'Dental Fillings',
      'Dental Crowns',
      'Dental Bridges',
      'Dental Implants',
      'Dentures',
      'Inlays and Onlays'
    ]
  },
  {
    id: 'orthodontics',
    title: 'Orthodontics',
    description: 'Straighten teeth and correct bite issues for better oral health',
    icon: Activity,
    color: 'bg-purple-100 text-purple-600',
    treatments: [
      'Traditional Braces',
      'Clear Aligners',
      'Retainers',
      'Bite Correction',
      'Space Maintainers',
      'Orthodontic Consultation'
    ]
  },
  {
    id: 'endodontics',
    title: 'Endodontics (Root Canal)',
    description: 'Save infected or damaged teeth with advanced root canal therapy',
    icon: Heart,
    color: 'bg-red-100 text-red-600',
    treatments: [
      'Root Canal Treatment',
      'Pulp Therapy',
      'Apicoectomy',
      'Root Canal Retreatment',
      'Dental Trauma Care',
      'Endodontic Surgery'
    ]
  },
  {
    id: 'pediatric',
    title: 'Pediatric Dentistry',
    description: 'Specialized dental care for children in a fun, comfortable environment',
    icon: Baby,
    color: 'bg-yellow-100 text-yellow-600',
    treatments: [
      'Pediatric Checkups',
      'Fluoride Treatment for Kids',
      'Dental Sealants for Children',
      'Cavity Treatment',
      'Space Maintainers',
      'Dental Education'
    ]
  },
  {
    id: 'surgery',
    title: 'Oral Surgery',
    description: 'Surgical procedures for complex dental conditions',
    icon: Scissors,
    color: 'bg-orange-100 text-orange-600',
    treatments: [
      'Tooth Extractions',
      'Wisdom Tooth Removal',
      'Dental Implant Surgery',
      'Gum Surgery',
      'Bone Grafting',
      'Pre-Prosthetic Surgery'
    ]
  },
  {
    id: 'emergency',
    title: 'Emergency Dental Care',
    description: 'Immediate care for dental emergencies and urgent situations',
    icon: AlertCircle,
    color: 'bg-red-100 text-red-600',
    treatments: [
      'Severe Toothache Relief',
      'Broken Tooth Repair',
      'Lost Filling/Crown',
      'Dental Trauma',
      'Abscess Treatment',
      'Emergency Extractions'
    ]
  }
]

export function ServicesPage() {

  return (
    <div className="min-h-screen">
      <Header currentPage="services" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-yellow-500/8 to-yellow-600/5 animate-pulse"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-yellow-600/15 rounded-full blur-xl animate-bounce" style={{animationDelay: '0s'}}></div>
          <div className="absolute top-1/2 right-20 w-16 h-16 bg-gradient-to-br from-yellow-500/15 to-yellow-700/10 rounded-full blur-lg animate-bounce" style={{animationDelay: '2s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fade-in-up" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>
              Our <span className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">Services</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
              Comprehensive dental care for the whole family with modern technology and gentle techniques
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services" className="py-16 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-10 w-32 h-32 bg-gradient-to-br from-yellow-300/10 via-yellow-400/8 to-yellow-500/6 rounded-full blur-2xl animate-float" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-1/4 right-10 w-24 h-24 bg-gradient-to-br from-yellow-400/15 via-yellow-500/12 to-yellow-600/10 rounded-full blur-xl animate-float" style={{animationDelay: '3s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon
              return (
                <div key={service.id} className={`bg-white rounded-lg shadow-2xl hover:shadow-3xl transition-all duration-500 overflow-hidden border-2 border-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-400 hover:border-gradient-to-r hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-600 transform hover:scale-105 hover:-translate-y-2 group animate-fade-in-up cursor-pointer`} style={{animationDelay: `${0.2 + index * 0.1}s`, animationFillMode: 'both'}}>
                  <div className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mb-6 group-hover:animate-bounce shadow-lg group-hover:shadow-xl transition-all duration-300">
                      <Icon className="h-8 w-8 text-white group-hover:text-gray-100 transition-colors duration-300 group-hover:scale-110" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">{service.title}</h3>
                    <p className="text-gray-600 mb-6 group-hover:text-gray-700 transition-colors duration-300">{service.description}</p>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900 mb-3 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Treatments Include:</h4>
                      <ul className="space-y-1">
                        {service.treatments.map((treatment, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                            <div className="w-2 h-2 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full mr-3 group-hover:animate-pulse"></div>
                            {treatment}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="px-8 pb-8">
                    <Button asChild className="w-full bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 text-white font-semibold transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <Link to="/book-appointment">
                        <Calendar className="mr-2 h-4 w-4" />
                        Book Consultation
                      </Link>
                    </Button>
                  </div>
                  <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-full"></div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 right-1/4 w-40 h-40 bg-gradient-to-br from-yellow-200/10 via-yellow-300/8 to-yellow-400/6 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>Why Choose Our Services?</h2>
            <p className="text-xl text-gray-600 animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>We provide exceptional dental care with modern technology and compassionate service</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-lg shadow-md border-2 border-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-400 hover:shadow-2xl hover:border-gradient-to-r hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-600 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 group animate-fade-in-up cursor-pointer" style={{animationDelay: '0.6s', animationFillMode: 'both'}}>
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:animate-bounce shadow-lg transition-all duration-300">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Painless Treatment</h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">Advanced techniques and gentle care ensure comfortable, pain-free dental procedures.</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-md border-2 border-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-400 hover:shadow-2xl hover:border-gradient-to-r hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-600 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 group animate-fade-in-up cursor-pointer" style={{animationDelay: '0.8s', animationFillMode: 'both'}}>
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:animate-bounce shadow-lg transition-all duration-300">
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Expert Care</h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">Dr. Aishwarya's expertise and continuous learning ensure the highest quality treatments.</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-md border-2 border-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-400 hover:shadow-2xl hover:border-gradient-to-r hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-600 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 group animate-fade-in-up cursor-pointer" style={{animationDelay: '1.0s', animationFillMode: 'both'}}>
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:animate-bounce shadow-lg transition-all duration-300">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Personalized Care</h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">Every treatment plan is customized to meet your specific needs and preferences.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Your Dentist */}
      <section className="py-16 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-10 w-32 h-32 bg-gradient-to-br from-yellow-300/10 via-yellow-400/8 to-yellow-500/6 rounded-full blur-2xl animate-float" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-1/4 right-10 w-24 h-24 bg-gradient-to-br from-yellow-400/15 via-yellow-500/12 to-yellow-600/10 rounded-full blur-xl animate-float" style={{animationDelay: '3s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-left" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 hover:bg-gradient-to-r hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 hover:bg-clip-text hover:text-transparent transition-all duration-300">Meet Dr. Aishwarya V</h2>
              <p className="text-lg text-gray-600 mb-6 animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
                With years of experience in dental care and a passion for patient comfort, Dr. Aishwarya provides comprehensive dental services with the latest technology and techniques.
              </p>
              <div className="space-y-4">
                <div className="flex items-center group hover:bg-white hover:p-4 hover:rounded-lg hover:shadow-lg transition-all duration-300 animate-fade-in-up" style={{animationDelay: '0.6s', animationFillMode: 'both'}}>
                  <GraduationCap className="h-6 w-6 bg-gradient-to-br from-yellow-600 via-yellow-700 to-yellow-800 text-white rounded p-1 mr-3 group-hover:animate-bounce" />
                  <span className="text-gray-700 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Bachelor of Dental Surgery (BDS)</span>
                </div>
                <div className="flex items-center group hover:bg-white hover:p-4 hover:rounded-lg hover:shadow-lg transition-all duration-300 animate-fade-in-up" style={{animationDelay: '0.7s', animationFillMode: 'both'}}>
                  <Award className="h-6 w-6 bg-gradient-to-br from-yellow-600 via-yellow-700 to-yellow-800 text-white rounded p-1 mr-3 group-hover:animate-pulse" />
                  <span className="text-gray-700 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Fellowship in Aesthetic and Adhesive Dentistry (FAAD) (Germany)</span>
                </div>
                <div className="flex items-center group hover:bg-white hover:p-4 hover:rounded-lg hover:shadow-lg transition-all duration-300 animate-fade-in-up" style={{animationDelay: '0.8s', animationFillMode: 'both'}}>
                  <Heart className="h-6 w-6 bg-gradient-to-br from-yellow-600 via-yellow-700 to-yellow-800 text-white rounded p-1 mr-3 group-hover:animate-bounce" />
                  <span className="text-gray-700 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Gentle, patient-centered approach</span>
                </div>
              </div>
              <div className="mt-8 animate-fade-in" style={{animationDelay: '1.0s', animationFillMode: 'both'}}>
                <Button size="lg" asChild className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 text-white font-semibold transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-yellow-500/50">
                  <Link to="/book-appointment">
                    <Calendar className="mr-2 h-5 w-5" />
                    Book Consultation
                  </Link>
                </Button>
              </div>
            </div>
            <div className="text-center animate-fade-in-right" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
              <div className="bg-white rounded-2xl p-8 shadow-2xl border-2 border-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 hover:shadow-3xl hover:shadow-yellow-400/20 transition-all duration-500 transform hover:scale-105">
                <div className="w-48 h-48 mx-auto mb-6 relative group">
                  <img 
                    src={drAishwaryaImage} 
                    alt="Dr. Aishwarya V - Experienced Dental Specialist at Sri Sai Sabari Dental Clinic"
                    className="w-full h-full object-cover rounded-full shadow-lg border-4 border-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 transform group-hover:scale-105 transition-all duration-300"
                  />
                  <div className="absolute inset-0 rounded-full border-4 border-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 animate-ping opacity-20"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2 hover:bg-gradient-to-r hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 hover:bg-clip-text hover:text-transparent transition-all duration-300">Dr. Aishwarya V</h3>
                <p className="text-lg text-gray-600 mb-4 hover:text-gray-700 transition-colors duration-300">BDS, FAAD (Germany)</p>
                <p className="text-sm text-gray-500 mb-4">TN Dental Council Reg: 26411</p>
                <p className="text-gray-600 hover:text-gray-700 transition-colors duration-300">Your trusted dental care specialist</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Care */}
      <section className="py-16 bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 right-1/4 w-40 h-40 bg-gradient-to-br from-yellow-200/10 via-yellow-300/8 to-yellow-400/6 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-1/4 left-1/4 w-28 h-28 bg-gradient-to-br from-yellow-300/15 via-yellow-400/12 to-yellow-500/10 rounded-full blur-2xl animate-float" style={{animationDelay: '4s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in-up group" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>
              <AlertCircle className="h-10 w-10 text-white group-hover:animate-pulse" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up hover:bg-gradient-to-r hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 hover:bg-clip-text hover:text-transparent transition-all duration-300" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>Dental Emergency?</h2>
            <p className="text-xl text-gray-600 mb-8 animate-fade-in-up" style={{animationDelay: '0.6s', animationFillMode: 'both'}}>
              We provide immediate care for dental emergencies. Don't wait - call us now!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{animationDelay: '0.8s', animationFillMode: 'both'}}>
              <Button size="lg" asChild className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 text-white font-semibold transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-yellow-500/50">
                <a href="tel:+91-9841817510">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now: +91-9841817510
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild className="border-2 border-yellow-500 text-yellow-600 hover:bg-gradient-to-r hover:from-yellow-500 hover:via-yellow-600 hover:to-yellow-700 hover:text-white hover:border-yellow-600 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-yellow-500/30">
                <Link to="/book-appointment">
                  <Calendar className="mr-2 h-5 w-5" />
                  Book Emergency Appointment
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/3 w-36 h-36 bg-gradient-to-br from-yellow-300/10 via-yellow-400/8 to-yellow-500/6 rounded-full blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-1/4 right-1/3 w-20 h-20 bg-gradient-to-br from-yellow-400/15 via-yellow-500/12 to-yellow-600/10 rounded-full blur-xl animate-float" style={{animationDelay: '3s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up hover:bg-gradient-to-r hover:from-yellow-600 hover:via-yellow-700 hover:to-yellow-800 hover:bg-clip-text hover:text-transparent transition-all duration-300" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>Ready to Schedule Your Appointment?</h2>
            <p className="text-xl text-gray-600 mb-8 animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
              Contact us today to discuss your dental needs and schedule your consultation
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center p-6 rounded-lg bg-white border-2 border-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-400 hover:shadow-2xl hover:border-gradient-to-r hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-600 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 group animate-fade-in-up cursor-pointer" style={{animationDelay: '0.6s', animationFillMode: 'both'}}>
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:animate-bounce shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <Phone className="h-8 w-8 text-white group-hover:text-gray-100 transition-colors duration-300 group-hover:scale-110" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Call Us</h3>
                <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">+91-9841817510</p>
                <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                </div>
              </div>
              <div className="text-center p-6 rounded-lg bg-white border-2 border-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-400 hover:shadow-2xl hover:border-gradient-to-r hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-600 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 group animate-fade-in-up cursor-pointer" style={{animationDelay: '0.8s', animationFillMode: 'both'}}>
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:animate-bounce shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <Mail className="h-8 w-8 text-white group-hover:text-gray-100 transition-colors duration-300 group-hover:scale-110" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Email Us</h3>
                <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">smile@sssdental.com</p>
                <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                </div>
              </div>
              <div className="text-center p-6 rounded-lg bg-white border-2 border-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-400 hover:shadow-2xl hover:border-gradient-to-r hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-600 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 group animate-fade-in-up cursor-pointer" style={{animationDelay: '1.0s', animationFillMode: 'both'}}>
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:animate-bounce shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <MapPin className="h-8 w-8 text-white group-hover:text-gray-100 transition-colors duration-300 group-hover:scale-110" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:bg-gradient-to-r group-hover:from-yellow-600 group-hover:via-yellow-700 group-hover:to-yellow-800 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">Visit Us</h3>
                <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">Puzhuthivakkam, Chennai</p>
                <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}